// src/components/BiddingPhase.jsx
import React, { useContext, useState, useEffect } from 'react';
import { GameContext } from '../context/GameContext';
import { motion, AnimatePresence } from 'framer-motion';

const BiddingPhase = () => {
  const { gameState, socket } = useContext(GameContext);
  const [selectedBid, setSelectedBid] = useState(null);
  const [error, setError] = useState(null);
  const [lastBidder, setLastBidder] = useState(null);

  const player = gameState?.players?.find(p => p.socketId === socket.id);
  const playerId = player?.id;
  const totalPlayers = gameState?.players?.length || 0;
  const minBid = totalPlayers === 4 ? 14 : 16;
  const currentBid = gameState?.state?.highestBid?.bid || (minBid - 1);
  const isPlayerTurn = gameState?.players?.[gameState.state.turn]?.id === playerId;

  useEffect(() => {
    const highest = gameState?.state?.highestBid;
    if (highest) {
      const bidder = gameState.players.find(p => p.id === highest.playerId);
      setLastBidder(bidder?.name || null);
    }
  }, [gameState?.state?.highestBid]);

  const handleBid = (bid) => {
    setError(null);
    if (!playerId) return;
    socket.emit('bid', {
      gameId: gameState.id,
      playerId,
      bid,
    });
    setSelectedBid(bid);
  };

  // Initialize selectedBid to minBid if currentBid is below minBid
  React.useEffect(() => {
    if (isPlayerTurn && !selectedBid && currentBid < minBid) {
      setSelectedBid(minBid);
    }
  }, [isPlayerTurn, currentBid, minBid, selectedBid]);

  const bidOptions = Array.from({ length: 28 - minBid + 1 }, (_, i) => minBid + i); // minBid to 28

  // Show objective only if there's an actual bid, otherwise show bidding instruction
  const highestBidPlayer = gameState?.state?.highestBid?.playerId;
  let objective = `Bid to become the caller, or pass. Minimum bid: ${minBid}`;

  if (highestBidPlayer) {
    const bidderName = gameState.players.find(p => p.id === highestBidPlayer)?.name;
    const currentTeam = player?.team;
    const callerTeam = gameState.players.find(p => p.id === highestBidPlayer)?.team;

    if (callerTeam === currentTeam) {
      objective = `${bidderName} is leading with ${currentBid}. Bid higher to take the call!`;
    } else {
      objective = `${bidderName} (opponent) is bidding ${currentBid}. Bid higher or let them have it.`;
    }
  }

  const nextPlayer = gameState?.players?.[gameState.state.turn];
  const nextPlayerName = nextPlayer?.name || 'Unknown';

  const uniqueTeams = new Set(gameState.players?.map(p => p.team));
  const teamMode = totalPlayers === 4 && uniqueTeams.size === 2 ? '2v2' : '3v3';

  const playerHand = player?.hand || [];

  return (
    <div className="bidding-phase p-4 bg-white shadow rounded-lg">
      <h3 className="text-lg font-semibold mb-2">Bidding Phase ({teamMode})</h3>
      <AnimatePresence>
        {lastBidder && (
          <motion.div
            key={lastBidder + currentBid}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="text-sm text-green-600 font-medium mb-2"
          >
            Last Bid by {lastBidder}: {currentBid}
          </motion.div>
        )}
      </AnimatePresence>
      <p className="text-sm text-gray-600 mb-2">{objective}</p>
      <p className="text-sm text-blue-500 font-medium mb-4">Next to bid: {nextPlayerName} (anticlockwise)</p>

      {playerHand.length > 0 && (
        <div className="mb-4">
          <h4 className="font-semibold mb-1">Your Cards</h4>
          <div className="flex flex-wrap justify-center gap-2">
            {playerHand.map((card, i) => {
              const isRed = card.suit === '♥' || card.suit === '♦';
              return (
                <div
                  key={i}
                  className="border px-2 py-1 rounded bg-white text-lg font-bold"
                  style={{
                    color: isRed ? '#dc2626' : '#000000',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  {card.value}{card.suit}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isPlayerTurn && (
        <motion.div
          key="bidding-options"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{
            backgroundColor: '#f0f9ff',
            border: '3px solid #3b82f6',
            borderRadius: '16px',
            padding: '24px',
            marginTop: '20px'
          }}
        >
          <h4 style={{
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: '#1e40af',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Your Turn to Bid
          </h4>

          {/* Bid Stepper */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            gap: '16px'
          }}>
            <button
              onClick={() => {
                const currentValue = selectedBid || Math.max(minBid, currentBid + 1);
                const newBid = currentValue - 1;
                if (newBid >= minBid && newBid > currentBid) {
                  setSelectedBid(newBid);
                }
              }}
              disabled={(selectedBid || Math.max(minBid, currentBid + 1)) <= currentBid + 1}
              style={{
                width: '64px',
                height: '64px',
                fontSize: '2rem',
                fontWeight: 'bold',
                backgroundColor: selectedBid && selectedBid > currentBid + 1 ? '#3b82f6' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: selectedBid && selectedBid > currentBid + 1 ? 'pointer' : 'not-allowed',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              −
            </button>

            <div style={{
              backgroundColor: 'white',
              border: '4px solid #3b82f6',
              borderRadius: '16px',
              padding: '20px 32px',
              minWidth: '160px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
              <div style={{
                fontSize: '3.5rem',
                fontWeight: 'bold',
                color: '#1e40af',
                lineHeight: '1'
              }}>
                {selectedBid || Math.max(minBid, currentBid + 1)}
              </div>
              <div style={{
                fontSize: '0.9rem',
                color: '#6b7280',
                marginTop: '8px',
                fontWeight: '600'
              }}>
                Your Bid
              </div>
            </div>

            <button
              onClick={() => {
                const currentValue = selectedBid || Math.max(minBid, currentBid + 1);
                const newBid = currentValue + 1;
                if (newBid <= 28) {
                  setSelectedBid(newBid);
                }
              }}
              disabled={(selectedBid || Math.max(minBid, currentBid + 1)) >= 28}
              style={{
                width: '64px',
                height: '64px',
                fontSize: '2rem',
                fontWeight: 'bold',
                backgroundColor: !selectedBid || selectedBid < 28 ? '#3b82f6' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: !selectedBid || selectedBid < 28 ? 'pointer' : 'not-allowed',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              +
            </button>
          </div>

          <div style={{
            fontSize: '0.95rem',
            color: '#6b7280',
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            Minimum: {minBid} | Current: {currentBid}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <button
              onClick={() => handleBid('thani')}
              style={{
                padding: '16px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                minHeight: '56px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              🎯 Thani
            </button>
            <button
              onClick={() => {
                const bidValue = selectedBid || Math.max(minBid, currentBid + 1);
                if (bidValue > currentBid) {
                  handleBid(bidValue);
                } else {
                  setError('Bid must be higher than current bid');
                }
              }}
              disabled={(selectedBid || Math.max(minBid, currentBid + 1)) <= currentBid}
              style={{
                padding: '16px',
                backgroundColor: (selectedBid || Math.max(minBid, currentBid + 1)) > currentBid ? '#10b981' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: (selectedBid || Math.max(minBid, currentBid + 1)) > currentBid ? 'pointer' : 'not-allowed',
                minHeight: '56px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              ✓ Bid
            </button>
            <button
              onClick={() => handleBid(null)}
              style={{
                padding: '16px',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                minHeight: '56px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            >
              ✖ Pass
            </button>
          </div>
          {error && <p style={{ color: '#dc2626', fontSize: '0.95rem', marginTop: '12px', textAlign: 'center', fontWeight: '600' }}>{error}</p>}
        </motion.div>
      )}

      {!isPlayerTurn && (
        <p className="text-sm text-gray-500">Waiting for other players to bid...</p>
      )}
    </div>
  );
};

export default BiddingPhase;
